import { PlusCircleOutlined } from '@ant-design/icons';
import { ProFormSelect, ProFormText, ProForm } from '@ant-design/pro-form';
import { message, Space, Modal } from 'antd';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import classnames from 'classnames';
import { unset, get } from 'lodash-es';
import React, { FC } from 'react';
import CloseCircleIcon from '@/assets/images/public/close-circle.svg';
import InfoCircleFillIcon from '@/assets/images/public/info-circle-fill-max.react.svg?react';
import { RecordType } from '@/presentation/pages/setting/app-deliver-config/components/configuration-content';
import { CustomizeData } from '@/presentation/pages/setting/app-deliver-config/components/customize-item';
import styles from '@/presentation/pages/setting/app-deliver-config/components/index.module.scss';
import { getSubConfigItemRules, transform2Value, TYPE } from '@/presentation/pages/setting/app-deliver-config/utils';
import { useStores } from '@/presentation/store';

interface IProps {
  name: string;
  config: RecordType; // 当前的配置项，包含默认配置项和全量配置项
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  fullConfig: RecordType; // 全量配置项
  dataSource: RecordType; // 原始数据
  form?: FormInstance;
  readonly?: boolean;
  options?: { label: string, value: string }[];
}

export const renderModal = (
  title: string,
  content: string,
  onOk: () => Promise<boolean> | void,
) => Modal.confirm({
  title: <Space className={styles.delModalTitle} align={'center'}>
    <InfoCircleFillIcon/>
    <span>{title}</span>
  </Space>,
  content: <div style={{ marginLeft: 32 }}>{content}</div>,
  okText: '确定',
  cancelText: '取消',
  width: 540,
  onOk,
  closable: true,
  icon: undefined,
});

export const RenderSubConfigItem: FC<IProps> = (props: IProps) => {
  const { form, name, config, options, fullConfig, data, setData, readonly } = props;
  const getData = (newConfig: RecordType) => data?.map((item: RecordType) => {
    if (item[name]) {
      return {
        [name]: newConfig,
      };
    }
    return item;
  });

  const { configs: operation = {} } = useStores().ConfigStore;
  const { configFieldInfo = {} } = operation;

  return <Space direction='vertical' size={0}>
    {Object.keys(config).map((item, index) => {
      const subConfigItemType = get(configFieldInfo, `${item}.type`, TYPE.UNKNOWN);

      const onChangeName = (key: string) => {
        if (key in config) {
          message.warning('选择配置项属性重复');
          return;
        }
        const newConfig: RecordType = {};
        Object.keys(config).forEach((name) => {
          if (item === name) {
            newConfig[key] = fullConfig?.[key];
          } else {
            newConfig[name] = config[name];
          }
        });
        const newData = getData(newConfig);
        setData(newData);
      };

      const onChangeValue = (e: any) => {
        config[item] = transform2Value(e.target.value, subConfigItemType);
        const newData = getData(config);
        setData(newData);
      };

      const onClickDelete = () => {
        const onDelete = async () => {
          unset(config, item);
          const newData = getData(config);
          setData(newData);
          return true;
        };
        if (item === 'name') {
          message.warning('默认配置项不支持删除');
          return;
        }
        renderModal(`删除 ${item}`, '确定删除此项配置属性吗？', onDelete);
      };

      const key = options?.find(option => Object.keys(config).every(key => key !== option.value))?.value;

      const onClickAdd = () => {
        if (!key) {
          message.warning('该配置项下的属性已全部添加');
          return;
        }
        config[key] = fullConfig?.[key];
        const newData = getData(config);
        setData(newData);
      };

      const isPushIcon = (index === Object.keys(config)?.length - 1 && !!key && !readonly);
      if (['array', 'object array', 'string array'].includes(subConfigItemType)) {
        return <CustomizeData
          configs={[{ [item]: config[item] }]}
          setConfigs={(itemData: any) => {
            setData((configs: any[]) => configs.map((config) => {
              if (config[name]) {
                return { [name]: { ...config[name], ...itemData?.[0]?.[item] || {} } };
              }
              return config;
            }));
          }}
          config={{ [item]: { [item]: config[item] } } || {}}
          form={props.form}
          typeDisabled={true}
          fatherDel={onClickDelete}
          fatherAdd={onClickAdd}
          fatherNameOption={options ?? []}
          fatherNameChange={onChangeName}
          fatherNameValue={item}
          fatherAddVisible={isPushIcon}
          readonly={readonly}
          childName={subConfigItemType === 'object array' ? fullConfig?.[item]?.[0] : undefined}
        />;
      }
      return <ProForm submitter={false} form={form} style={{ paddingLeft: 58 }}>
        <Space direction='horizontal' className={styles.configItem}>
          <ProFormSelect
            name={`${name}${item}`}
            options={options ?? []}
            disabled={item === 'name' || readonly}
            width={280}
            allowClear={false}
            initialValue={item}
            fieldProps={{
              value: item,
              dropdownMatchSelectWidth: 300,
              onChange: onChangeName,
              showArrow: !readonly,
            }}
          />
          <ProFormText
            name={`${name}_${item}_type`}
            width={165}
            allowClear={false}
            initialValue={subConfigItemType}
            disabled={true}
            fieldProps={{
              value: subConfigItemType,
            }}
          />
          <ProFormText
            name={`${name}_${item}_value`}
            width={300}
            allowClear={false}
            initialValue={typeof config[item] === 'object' ? JSON.stringify(config[item]) : config[item]}
            disabled={item === 'name' || readonly}
            fieldProps={{
              value: typeof config[item] === 'object' ? JSON.stringify(config[item]) : config[item],
              onChange: onChangeValue,
            }}
            rules={getSubConfigItemRules(item, subConfigItemType)}
          />
          <img
            src={CloseCircleIcon}
            alt={''}
            onClick={onClickDelete}
            className={classnames({
              [styles.delConfigItem]: true,
              [styles.configItemDisable]: readonly,
            })}
          />
          <PlusCircleOutlined
            onClick={onClickAdd}
            className={classnames({
              [styles.addConfigItem]: true,
              [styles.itemVisible]: isPushIcon,
            })}
          />
        </Space>

    </ProForm>;
    })}
  </Space>;
};
